// The Recipes library (redesign slice 7a; wireframes 8 and 17;
// docs/2026-10-04-recipes-design.md). One list on every screen size: each
// row opens the recipe page (#8) and says how the recipe stands for you
// ("In your plan: tonight", "Shared with you", "From a household member").
// Search covers titles and sources; Favorites, dish type and In season are
// one tap away, and "All filters" holds the rest, including Whose recipes
// and Total time (#11). Recipes shared with you in the last month show
// first (#10). "+ Add" opens the Add sheet.
//
// On a wide screen the filters sit in a panel beside the list; on a phone
// they open in a sheet. Editing happens on the recipe page.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { Sheet } from "../components/Sheet";
import { LibraryFilters, Chip } from "../components/recipes/LibraryFilters";
import { AddRecipeSheet } from "../components/recipes/AddRecipeSheet";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, RECIPE_TYPES, RECIPE_TYPE_LABELS, SPACE } from "../lib/constants";
import { seasonOf } from "../lib/pickerView";
import { NO_LIBRARY_FILTERS, activeFilterCount, cuisinesIn, filterLibrary, libraryMeta, libraryStatus, nextPlanned, recentlyShared, sortLibrary, type LibraryFilters as Filters, type SortBy, type Viewer } from "../lib/recipeLibrary";
import type { Recipe } from "../lib/types";
import { smallLabel, supporting } from "../components/recipeStyles";
import { inputStyle, textButton } from "../components/plan/planStyles";

// Below this width the filters open in a sheet rather than a side panel.
const SIDE_PANEL_MIN_WIDTH = 1000;

const typeLabel = (t: string) => (RECIPE_TYPE_LABELS as Record<string, string>)[t] ?? t;
// Dish types in the plural, for "All mains" and the dish chip.
const PLURALS: Record<string, string> = { entree: "Mains", side: "Sides", dessert: "Desserts", appetizer: "Appetizers", beverage: "Beverages", cocktail: "Cocktails", medicinal: "Medicinal", preserves: "Preserves", candy: "Candy", other: "Other" };
const plural = (t: string) => (PLURALS[t] ?? typeLabel(t)).toLowerCase();

const primaryButton = { minHeight: LAYOUT.tapTarget, border: "none", borderRadius: RADIUS.md, padding: "0 var(--space-xxl)", background: COLORS.accent, color: COLORS.onAccent, fontSize: FONT_SIZE.base, fontWeight: 700, cursor: "pointer" } as const;

export function RecipesPage() {
  const mp = useMealPlanner();
  const isMobile = useIsMobile();
  const narrow = useIsMobile(SIDE_PANEL_MIN_WIDTH - 1);
  const [filters, setFilters] = useState<Filters>(NO_LIBRARY_FILTERS);
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const now = useMemo(() => new Date(), []);
  const viewer: Viewer = useMemo(() => ({ userId: mp.userId, sharedWithMe: mp.sharedWithMeIds, isFavorite: mp.isFavorite }), [mp.userId, mp.sharedWithMeIds, mp.isFavorite]);
  const planned = useMemo(() => nextPlanned(mp.plansByWeek, now), [mp.plansByWeek, now]);
  const cuisines = useMemo(() => cuisinesIn(mp.recipes), [mp.recipes]);
  const shown = useMemo(() => sortLibrary(filterLibrary(mp.recipes, filters, viewer, now), sortBy), [mp.recipes, filters, viewer, now, sortBy]);
  const filterCount = activeFilterCount(filters);
  const searching = !!filters.query.trim() || filterCount > 0;
  const recent = searching ? [] : recentlyShared(mp.recipes, viewer, now);
  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const clearFilters = () => setFilters((f) => ({ ...NO_LIBRARY_FILTERS, query: f.query }));
  const countLabel = `${shown.length} ${filters.dishType ? (shown.length === 1 ? typeLabel(filters.dishType).toLowerCase() : plural(filters.dishType)) : shown.length === 1 ? "recipe" : "recipes"}`;

  const row = (r: Recipe) => {
    const status = libraryStatus(r, viewer, planned, mp.mySharedRecipeIds);
    const fav = mp.isFavorite(r);
    return (
      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: SPACE.md, borderBottom: `1px solid ${COLORS.line}` }}>
        <Link to={`/recipes/${r.id}`} style={{ flex: 1, minWidth: 0, display: "block", padding: "var(--space-lg) 0", color: COLORS.ink, textDecoration: "none" }}>
          <span style={{ display: "block", fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600, lineHeight: 1.25 }}>{r.title}</span>
          <span style={{ ...supporting, display: "block" }}>{libraryMeta(r)}</span>
          {status && <span style={{ display: "block", fontSize: FONT_SIZE.sm, fontWeight: 700, color: status.startsWith("In your plan") ? COLORS.accent : COLORS.soft }}>{status}</span>}
        </Link>
        <button
          type="button"
          aria-pressed={fav}
          aria-label={fav ? `Remove ${r.title} from favorites` : `Add ${r.title} to favorites`}
          onClick={() => mp.toggleFavorite(r.id)}
          style={{ width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, border: "none", background: "transparent", color: fav ? COLORS.accent : COLORS.soft, fontSize: FONT_SIZE.h3, cursor: "pointer", flexShrink: 0 }}
        >
          {fav ? "★" : "☆"}
        </button>
      </div>
    );
  };

  const toolbar = (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <input type="search" aria-label="Search recipes" placeholder="Search titles and sources" value={filters.query} onChange={(e) => set({ query: e.target.value })} style={{ ...inputStyle, width: "100%" }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm, alignItems: "center" }}>
        <Chip on={filters.favorites} onClick={() => set({ favorites: !filters.favorites })}>
          Favorites
        </Chip>
        <select
          aria-label="Dish type"
          value={filters.dishType}
          onChange={(e) => set({ dishType: e.target.value })}
          style={{ minHeight: LAYOUT.tapTarget, borderRadius: RADIUS.pill, padding: "0 var(--space-xl)", fontSize: FONT_SIZE.sm, fontWeight: 700, border: `1px solid ${filters.dishType ? COLORS.accent : COLORS.line}`, background: filters.dishType ? COLORS.accentTint : "transparent", color: filters.dishType ? COLORS.accent : COLORS.soft, cursor: "pointer" }}
        >
          <option value="">All dishes</option>
          {RECIPE_TYPES.map((t) => (
            <option key={t} value={t}>
              {PLURALS[t] ?? typeLabel(t)}
            </option>
          ))}
        </select>
        <Chip on={filters.inSeason} onClick={() => set({ inSeason: !filters.inSeason, season: "" })}>
          In season
        </Chip>
        {narrow && (
          <Chip on={filterCount > 0} onClick={() => setFiltersOpen(true)}>
            All filters{filterCount > 0 ? ` · ${filterCount}` : ""}
          </Chip>
        )}
        <label style={{ ...supporting, marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: SPACE.sm }}>
          Sort
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} style={{ ...inputStyle, minHeight: 36 }}>
            <option value="newest">Newest first</option>
            <option value="name">A to Z</option>
            <option value="quickest">Quickest</option>
          </select>
        </label>
      </div>
    </div>
  );

  const list = (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, minWidth: 0 }}>
      {toolbar}

      {recent.length > 0 && (
        <section>
          <h2 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>Recently shared with you</h2>
          {recent.map(row)}
        </section>
      )}

      <section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: SPACE.md }}>
          <h2 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{searching ? countLabel : filters.dishType ? `All ${plural(filters.dishType)}` : "All recipes"}</h2>
          {searching && (
            <button type="button" onClick={() => setFilters(NO_LIBRARY_FILTERS)} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
              Clear all
            </button>
          )}
        </div>
        {shown.map(row)}
        {mp.recipes.length === 0 && <p style={supporting}>No recipes yet. Add your first one to get started.</p>}
        {mp.recipes.length > 0 && shown.length === 0 && <p style={supporting}>No recipes match{filters.inSeason ? ` (in season: ${seasonOf(now)})` : ""}. Try fewer filters.</p>}
      </section>
    </div>
  );

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
      <div style={{ display: "flex", alignItems: "center", gap: SPACE.xl }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h1, fontWeight: 600, lineHeight: 1.1 }}>Recipes</h1>
          <div style={supporting}>
            {mp.recipes.length} recipe{mp.recipes.length === 1 ? "" : "s"}
          </div>
        </div>
        <button type="button" onClick={() => setAdding(true)} style={primaryButton}>
          + Add
        </button>
      </div>

      {narrow ? (
        <div style={{ maxWidth: isMobile ? undefined : 720 }}>{list}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 720px) 300px", gap: SPACE.x4, alignItems: "start" }}>
          {list}
          <aside aria-label="Filter recipes" style={{ position: "sticky", top: "calc(var(--app-header-h, 0px) + var(--space-xl))", maxHeight: "calc(100vh - var(--app-header-h, 0px) - 2 * var(--space-xl))", overflowY: "auto", border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, background: COLORS.card, padding: "var(--space-xl) var(--space-xxl)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACE.md }}>
              <h2 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600 }}>Filter recipes</h2>
              {filterCount > 0 && (
                <button type="button" onClick={clearFilters} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
                  Clear all
                </button>
              )}
            </div>
            <LibraryFilters filters={filters} onChange={setFilters} cuisines={cuisines} />
          </aside>
        </div>
      )}

      {filtersOpen && narrow && (
        <Sheet
          title="Filter recipes"
          onClose={() => setFiltersOpen(false)}
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md }}>
              <button type="button" onClick={clearFilters} style={{ ...textButton, color: COLORS.soft }}>
                Clear all
              </button>
              <button type="button" onClick={() => setFiltersOpen(false)} style={primaryButton}>
                Show {shown.length} recipe{shown.length === 1 ? "" : "s"}
              </button>
            </div>
          }
        >
          <LibraryFilters filters={filters} onChange={setFilters} cuisines={cuisines} />
        </Sheet>
      )}

      {adding && <AddRecipeSheet onClose={() => setAdding(false)} />}
    </div>
  );
}
